<template>
  <div :class="['modal', { 'modal--wider': modal.wider }, `modal--theme${modal.theme}`]">
    <div :class="['modal__header', { 'modal__header--disableBorder': !modal.title }]">
      <div v-if="modal.title" class="modal__title">{{ modal.title }}</div>
      <span class="modal__close icon-line-close" @click="popModal" />
    </div>
    <div class="modal__content">
      <component
        :is="dynamicContent"
        ref="content"
        :data="data"
        @closeAllModals="closeAllModals"
        @enableButton="isButtonDisabled = false"
        @disableButton="isButtonDisabled = true"
      />
    </div>
    <div v-if="!modal.footerDisabled" class="modal__footer">
      <div class="modal__footerInner">
        <div :class="['modal__button']" v-for="button in modal.buttons" :key="button.text">
          <v-button
            :text="isMobile && button.mobileText ? button.mobileText : button.text"
            :theme="button.theme"
            :disabled="isButtonDisabled && button.disabled"
            @click="handleClick(button.action)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import mobileMixin from '~/mixins/mobile';
import VButton from '~/components/VButton';

export default {
  name: 'Modal',
  components: {
    VButton,
  },
  mixins: [mobileMixin()],
  props: {
    modal: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isButtonDisabled: true,
    };
  },
  computed: {
    dynamicContent() {
      return () => import(`./${this.modal.path}`);
    },
  },
  methods: {
    ...mapMutations({
      popModal: 'modal/popModal',
      closeAllModals: 'modal/closeAllModals',
    }),
    handleClick(action) {
      if (action === 'closeModal') this.popModal();
      else this.$refs.content.$emit(action);
    },
  },
};
</script>

<style lang="scss">
.modal {
  $root: &;
  max-width: 69.2rem;
  width: calc(100% - 6rem);
  max-height: calc(100vh - 6rem);
  background-color: $cWhite;
  border-radius: 5px;
  box-shadow: 0 0 60px rgba($cMainDark, 0.1);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  overflow: auto;

  &--wider {
    max-width: 100rem;
  }

  @include rwd('small-tablet') {
    width: 100%;
    height: 100%;
    max-height: unset;
    max-width: unset;
    top: 0;
    left: 0;
    transform: none;
  }

  &__header {
    padding: 3rem;
    display: flex;
    align-items: center;
    border: 1px solid $cAddLight;

    @include rwd('small-tablet') {
      padding: 2rem;
    }

    &--disableBorder {
      border-bottom: 0;
      padding: 3rem 3rem 0 3rem;

      @include rwd('small-tablet') {
        padding: 2rem 2rem 0 2rem;
      }
    }
  }

  &__title {
    font-size: 2.2rem;
    line-height: 2.9rem;
    font-weight: 600;
    color: $cMainDark;
    margin-right: 3rem;
  }

  &__close {
    margin-left: auto;
    color: $cMain;
    font-size: 1.5rem;
    cursor: pointer;
  }

  &__footer {
    padding: 1.5rem 3rem;
    background-color: rgba($cAddNeutral, 0.1);
    margin-top: auto;

    @include rwd('small-tablet') {
      padding: 1.5rem 2rem;
    }
  }

  &__footerInner {
    display: flex;
    justify-content: flex-end;
    margin: 0 -1rem;

    @include rwd('small-tablet') {
      margin: 0 -0.5rem;
    }
  }

  &__button {
    display: inline-flex;
    padding: 0 1rem;

    @include rwd('small-tablet') {
      padding: 0 0.5rem;
    }
  }

  &__content {
    padding: 3rem;
    overflow: auto;

    @include rwd('small-tablet') {
      padding: 2rem;
    }
  }

  &--themeLite {
    @include rwd('small-tablet') {
      max-height: calc(100vh - 6rem);
      width: 100%;
      height: auto;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    #{$root}__content {
      padding: 2rem 3rem 2rem;

      @include rwd('small-tablet') {
        padding: 2rem;
      }
    }
    #{$root}__header {
      border-bottom: 0;
      padding-bottom: 0;
    }

    #{$root}__footer {
      background-color: $cWhite;
      padding: 1.5rem 3rem 4rem 3rem;

      @include rwd('small-tablet') {
        padding: 1.5rem 2rem 2rem 2rem;
      }
    }
  }
}
</style>
